<template>
  <div class="student-id-ver-container">
    <div class="form-section">
      <!-- Header Section -->
      <div class="section-header">
        <div class="section-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
            />
          </svg>
        </div>
        <div class="section-title">
          <h3>Student ID Verification <span class="required">*</span></h3>
          <p>Upload your student ID to verify your enrollment status</p>
        </div>
      </div>

      <!-- File Upload Area -->
      <div
        class="file-upload-area"
        @drop="handleFileDrop"
        @dragover.prevent
        @dragenter.prevent
        :class="{
          'drag-over': isDragOver,
          'file-uploaded': studentIDFile,
          'upload-success': uploadSuccess,
        }"
      >
        <div class="upload-icon">
          <!-- Success checkmark animation -->
          <div v-if="uploadSuccess" class="success-checkmark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" />
              <path
                d="M9 12l2 2 4-4"
                stroke="#10B981"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <!-- Default upload icon -->
          <svg v-else viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
          </svg>
        </div>
        <div class="upload-text">
          <p v-if="!studentIDFile" class="drag-text">Drag and drop your student ID here</p>
          <p v-else-if="!uploadSuccess" class="drag-text">
            File selected: {{ studentIDFile.name }}
          </p>
          <p v-else class="drag-text success-text">File uploaded successfully!</p>
          <p v-if="!studentIDFile" class="or-text">or</p>
          <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept=".jpg,.jpeg,.png,.pdf"
            class="file-input"
          />
          <button v-if="!studentIDFile" @click="triggerFileInput" class="browse-btn">
            Browse Files
          </button>
          <button v-else @click="removeFile" class="remove-btn">Remove File</button>
          <p class="file-size-limit">Maximum file size: 10MB</p>
          <p v-if="studentIDError" class="error-message">{{ studentIDError }}</p>
        </div>
      </div>

      <!-- Photo Guidelines Section -->
      <div class="photo-guidelines-section">
        <h4>Photo Guidelines:</h4>
        <ul>
          <li>• Ensure your student ID is clearly visible and readable</li>
          <li>• Avoid glare or shadows on the ID</li>
          <li>• Make sure all text and details are legible</li>
          <li>• Accepted formats: JPG, PNG, PDF</li>
        </ul>
      </div>

      <!-- Student Holding ID Upload Area -->
      <div class="section-header-holding-id">
        <div class="section-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
            />
          </svg>
        </div>
        <div class="section-title">
          <h3>Student Holding ID Photo <span class="required">*</span></h3>
          <p>Upload a photo of yourself holding your student ID</p>
        </div>
      </div>

      <!-- Student Holding ID File Upload Area -->
      <div
        class="file-upload-area"
        @drop="handleHoldingIdFileDrop"
        @dragover.prevent
        @dragenter.prevent
        :class="{
          'drag-over': isHoldingIdDragOver,
          'file-uploaded': studentHoldingIdFile,
          'upload-success': holdingIdUploadSuccess,
        }"
      >
        <div class="upload-icon">
          <!-- Success checkmark animation -->
          <div v-if="holdingIdUploadSuccess" class="success-checkmark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" />
              <path
                d="M9 12l2 2 4-4"
                stroke="#10B981"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <!-- Default upload icon -->
          <svg v-else viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
          </svg>
        </div>
        <div class="upload-text">
          <p v-if="!studentHoldingIdFile" class="drag-text">Drag and drop your photo here</p>
          <p v-else-if="!holdingIdUploadSuccess" class="drag-text">
            File selected: {{ studentHoldingIdFile.name }}
          </p>
          <p v-else class="drag-text success-text">File uploaded successfully!</p>
          <p v-if="!studentHoldingIdFile" class="or-text">or</p>
          <input
            type="file"
            ref="holdingIdFileInput"
            @change="handleHoldingIdFileSelect"
            accept=".jpg,.jpeg,.png"
            class="file-input"
          />
          <button
            v-if="!studentHoldingIdFile"
            @click="triggerHoldingIdFileInput"
            class="browse-btn"
          >
            Browse Files
          </button>
          <button v-else @click="removeHoldingIdFile" class="remove-btn">Remove File</button>
          <p class="file-size-limit">Maximum file size: 10MB</p>
          <p v-if="holdingIDError" class="error-message">{{ holdingIDError }}</p>
        </div>
      </div>

      <!-- Student Holding ID Guidelines Section -->
      <div class="photo-guidelines-section">
        <h4>Student Holding ID Guidelines:</h4>
        <ul>
          <li>• Take a clear photo of yourself holding your student ID</li>
          <li>• Ensure your face and the ID are both clearly visible</li>
          <li>• Hold the ID close to your face for verification</li>
          <li>• Use good lighting and avoid shadows</li>
          <li>• Accepted formats: JPG, PNG (no PDF for photos)</li>
        </ul>
      </div>

      <!-- Footer Section -->
      <div v-if="studentIDFile || studentHoldingIdFile" class="footer-section">
        <div v-if="studentIDFile" class="upload-status">
          Student ID uploaded: {{ studentIDFile.name }}
        </div>
        <div v-if="studentHoldingIdFile" class="upload-status">
          Holding ID photo uploaded: {{ studentHoldingIdFile.name }}
        </div>
        <div class="instruction">
          Please review your uploaded files and make sure they are clear and readable.
        </div>
      </div>
    </div>

    <!-- Terms and Conditions Container -->
    <div class="terms-container">
      <label class="terms-checkbox">
        <input type="checkbox" v-model="termsAccepted" @change="updateTerms" />
        <span class="checkmark"></span>
        <span class="terms-text">
          <span class="required">*</span> I have read and agree to the
          <a href="/terms-conditions" class="terms-link" target="_blank" rel="noopener noreferrer"
            >Terms and Conditions</a
          >
          and
          <a href="/privacy-policy" class="terms-link" target="_blank" rel="noopener noreferrer"
            >Privacy Policy</a
          >
          in compliance with the Data Privacy Act of 2012 (RA 10173).
        </span>
      </label>
    </div>

    <!-- Buttons Container -->
    <div class="buttons-container">
      <!-- Back Button -->
      <button @click="goBack" class="back-btn">
        <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41,16.58L10.83,12L15.41,7.42L14,6L8,12L14,18L15.41,16.58Z" />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
          />
        </svg>
        <span>Back</span>
      </button>

      <!-- Submit Button -->
      <button
        @click="handleSubmit"
        class="continue-btn"
        :disabled="!studentIDFile || !studentHoldingIdFile || !termsAccepted"
      >
        <span>Complete Registration</span>
        <svg
          class="arrow-icon size-6"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { useStudentRegistrationStorage } from "@/composables/useRegistrationStorage";
import { logDebug } from '@/utils/secureLogger';

// Props
interface Props {
  modelValue: {
    studentIDFile: File | null;
    studentHoldingIdFile: File | null;
    termsAccepted: boolean;
  };
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "update:modelValue": [
    value: { studentIDFile: File | null; studentHoldingIdFile: File | null; termsAccepted: boolean }
  ];
  "verification-complete": [
    value: { studentIDFile: File | null; studentHoldingIdFile: File | null; termsAccepted: boolean }
  ];
  "go-back": [];
}>();

// Local state
const fileInput = ref<HTMLInputElement>();
const holdingIdFileInput = ref<HTMLInputElement>();
const isDragOver = ref(false);
const isHoldingIdDragOver = ref(false);
const studentIDFile = ref<File | null>(props.modelValue.studentIDFile || null);
const studentHoldingIdFile = ref<File | null>(props.modelValue.studentHoldingIdFile || null);
const termsAccepted = ref(props.modelValue.termsAccepted || false);
const uploadSuccess = ref(false);
const holdingIdUploadSuccess = ref(false);
const studentIDError = ref<string>("");
const holdingIDError = ref<string>("");

// Initialize registration storage
const { saveStudentVerification, loadStudentVerification } = useStudentRegistrationStorage();

// Load saved data on mount
onMounted(() => {
  const savedData = loadStudentVerification();
  if (savedData) {
    logDebug('Restoring student verification data from sessionStorage');
    termsAccepted.value = savedData.termsAccepted || false;
    // Note: Files cannot be restored from sessionStorage, user must re-upload
    // But we can show a message indicating files were previously selected
  }
});

// Auto-save verification data to sessionStorage (excluding actual files)
watch([termsAccepted, studentIDFile, studentHoldingIdFile], () => {
  saveStudentVerification({
    termsAccepted: termsAccepted.value,
    studentIDFileName: studentIDFile.value?.name,
    studentHoldingIdFileName: studentHoldingIdFile.value?.name,
  });
}, { deep: true });

// File handling
const handleFileDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (validateFile(file)) {
      studentIDFile.value = file;
      triggerSuccessAnimation();
      updateParent();
    }
  }
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    if (validateFile(file)) {
      studentIDFile.value = file;
      triggerSuccessAnimation();
      updateParent();
    }
  }
};

const triggerSuccessAnimation = () => {
  studentIDError.value = "";
  uploadSuccess.value = true;
  // Reset the animation after 2 seconds
  setTimeout(() => {
    uploadSuccess.value = false;
  }, 2000);
};

const removeFile = () => {
  studentIDFile.value = null;
  uploadSuccess.value = false;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
  updateParent();
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

// Student Holding ID file handling
const handleHoldingIdFileDrop = (event: DragEvent) => {
  event.preventDefault();
  isHoldingIdDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (validateHoldingIdFile(file)) {
      studentHoldingIdFile.value = file;
      triggerHoldingIdSuccessAnimation();
      updateParent();
    }
  }
};

const handleHoldingIdFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    if (validateHoldingIdFile(file)) {
      studentHoldingIdFile.value = file;
      triggerHoldingIdSuccessAnimation();
      updateParent();
    }
  }
};

const triggerHoldingIdSuccessAnimation = () => {
  holdingIDError.value = "";
  holdingIdUploadSuccess.value = true;
  // Reset the animation after 2 seconds
  setTimeout(() => {
    holdingIdUploadSuccess.value = false;
  }, 2000);
};

const removeHoldingIdFile = () => {
  studentHoldingIdFile.value = null;
  holdingIdUploadSuccess.value = false;
  if (holdingIdFileInput.value) {
    holdingIdFileInput.value.value = "";
  }
  updateParent();
};

const triggerHoldingIdFileInput = () => {
  holdingIdFileInput.value?.click();
};

const validateFile = (file: File): boolean => {
  // Clear previous error
  studentIDError.value = "";

  // Check file size (10MB = 10 * 1024 * 1024 bytes)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    studentIDError.value = "File size must be less than 10MB";
    return false;
  }

  // Check file type
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
  if (!allowedTypes.includes(file.type)) {
    studentIDError.value = "Please upload a JPG, PNG, or PDF file";
    return false;
  }

  return true;
};

const validateHoldingIdFile = (file: File): boolean => {
  // Clear previous error
  holdingIDError.value = "";

  // Check file size (10MB = 10 * 1024 * 1024 bytes)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    holdingIDError.value = "File size must be less than 10MB";
    return false;
  }

  // Check file type (only images for holding ID photo)
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
  if (!allowedTypes.includes(file.type)) {
    holdingIDError.value = "Please upload a JPG or PNG image file";
    return false;
  }

  return true;
};

// Update parent when values change
const updateParent = () => {
  emit("update:modelValue", {
    studentIDFile: studentIDFile.value,
    studentHoldingIdFile: studentHoldingIdFile.value,
    termsAccepted: termsAccepted.value,
  });
};

const updateTerms = () => {
  updateParent();
};

// Handle form submission
const handleSubmit = () => {
  if (!studentIDFile.value) {
    alert("Please upload your student ID");
    return;
  }

  if (!studentHoldingIdFile.value) {
    alert("Please upload a photo of yourself holding your student ID");
    return;
  }

  if (!termsAccepted.value) {
    alert("Please accept the terms and conditions");
    return;
  }

  // Emit the complete verification data
  emit("verification-complete", {
    studentIDFile: studentIDFile.value,
    studentHoldingIdFile: studentHoldingIdFile.value,
    termsAccepted: termsAccepted.value,
  });
};

// Handle back button
const goBack = () => {
  emit("go-back");
};

// Drag and drop visual feedback
const handleDragEnter = () => {
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};
</script>

<style scoped>
.student-id-ver-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 100vh;
  background: url("/student-reg-bg.png") no-repeat center center;
  background-size: cover;
  background-attachment: fixed;
  padding: 20px;
  padding-bottom: 30px;
  position: relative;
  margin-top: 140px; /* Added significant top margin to create space below steps */
}

.form-section {
  background: white;
  border: 2px solid #000000;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 32px;
  margin-bottom: 24px;
  margin-top: 20px; /* Additional top margin for the form itself */
  overflow: hidden;
  max-width: 1000px;
  width: 100%;
}

/* Header Section */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  gap: 12px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #e3effe;
  border-radius: 18px 18px 0 0;
  margin: -30px -30px 24px -30px;
}

.section-header-holding-id {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  margin-top: 40px !important;
  gap: 12px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #e3effe;
  border-radius: 18px 18px 0 0;
  margin: -30px -30px 24px -30px;
}

.section-icon {
  width: 24px;
  height: 24px;
  background: transparent;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3861fb;
}

.section-icon svg {
  width: 24px;
  height: 24px;
}

.section-title h3 {
  margin: 0;
  color: #1f42b3;
  font-size: 18px;
  font-weight: 600;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.section-title p {
  margin: 4px 0 0 0;
  color: #1f42b3;
  font-size: 14px;
  font-style: italic;
}

/* File Upload Area */
.file-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  background: #fafafa;
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: #9ca3af;
}

.file-upload-area.drag-over {
  border-color: #1e40af;
  background: #f8fafc;
}

.file-upload-area.file-uploaded {
  border-color: #10b981;
  background: #f0fdf4;
}

.file-upload-area.upload-success {
  border-color: #10b981;
  background: #f0fdf4;
  animation: successPulse 0.6s ease-in-out;
}

.upload-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e0e7ff;
  border-radius: 50%;
}

.upload-icon svg {
  width: 32px;
  height: 32px;
  color: #3b82f6;
}

.success-checkmark {
  width: 100%;
  height: 100%;
  animation: checkmarkDraw 0.8s ease-in-out;
}

.success-checkmark svg {
  width: 100%;
  height: 100%;
}

.upload-text .drag-text {
  font-size: 16px;
  color: #111827;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.upload-text .success-text {
  color: #10b981;
  font-weight: 600;
}

.upload-text .or-text {
  font-size: 14px;
  color: #3b82f6;
  margin: 0 0 12px 0;
}

.file-input {
  display: none;
}

.browse-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s ease;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.browse-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.remove-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 16px;
}

.remove-btn:hover {
  background: #dc2626;
}

.file-size-limit {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-weight: 400;
}

.error-message {
  font-size: 14px;
  color: #dc2626;
  margin-top: 8px;
  font-weight: 500;
}

/* Photo Guidelines */
.photo-guidelines-section {
  background: #e8f4ff;
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
}

.photo-guidelines-section h4 {
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.photo-guidelines-section ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.photo-guidelines-section li {
  color: #1e40af;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 8px;
  padding-left: 0;
}

/* Footer Section */
.footer-section {
  margin-top: 20px;
  text-align: center;
}

.upload-status {
  font-size: 14px;
  color: #1f42b3;
  font-weight: 600;
  margin-bottom: 8px;
}

.instruction {
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

/* Terms and Conditions */
.terms-container {
  background: white;
  border: 2px solid #000000;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 24px 32px;
  margin: 0 auto 30px auto;
  max-width: 1000px;
  width: 100%;
  display: block;
  position: relative;
}

.terms-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  position: relative;
}

.terms-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
  margin: 0;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background: white;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.2s ease;
}

.terms-checkbox input[type="checkbox"]:checked ~ .checkmark {
  background: #1e40af;
  border-color: #1e40af;
}

.terms-checkbox input[type="checkbox"]:checked ~ .checkmark::after {
  content: "";
  position: absolute;
  left: 8px;
  top: 5px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.terms-text {
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.terms-link {
  color: #1e40af;
  text-decoration: underline;
  font-weight: 500;
}

.terms-link:hover {
  color: #1d4ed8;
}

/* Buttons Container */
.buttons-container {
  display: flex;
  justify-content: space-between;
  max-width: 1000px;
  width: 100%;
  margin: 0 auto 50px auto;
}

/* Back Button */
.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: auto;
  min-width: 180px;
  padding: 18px 32px;
  background: white;
  color: #4f46e5;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
  margin: 0;
  justify-content: center;
}

.back-btn:hover {
  background: #4f46e5;
  color: white;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

/* Continue Button */
.continue-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: auto;
  min-width: 250px;
  padding: 18px 32px;
  background: #4f46e5;
  color: white;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
  box-shadow: 4px 4px 0px #000000;
  margin: 0;
}

.continue-btn:hover:not(:disabled) {
  background: #3730a3;
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.continue-btn:disabled,
.continue-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #9ca3af;
  border-color: #6b7280;
  box-shadow: 2px 2px 0px #6b7280;
  transform: none;
}

.continue-btn.disabled:hover {
  background: #9ca3af;
  transform: none;
}

.arrow-icon {
  width: 24px;
  height: 24px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes successPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes checkmarkDraw {
  0% {
    stroke-dasharray: 0 50;
    stroke-dashoffset: 0;
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    stroke-dasharray: 50 0;
    stroke-dashoffset: 0;
    opacity: 1;
  }
}

/* Responsive Design */
/* Mobile Responsive */
@media (max-width: 768px) {
  .student-id-ver-container {
    padding: 10px;
    min-height: 100vh;
    padding-bottom: 140px;
    margin-top: 120px; /* Slightly reduced but still significant top margin on tablets */
  }

  .form-section {
    padding: 20px;
    margin-bottom: 20px;
  }

  .terms-container {
    padding: 20px;
    margin: 0 auto 80px auto;
  }

  .upload-area {
    padding: 32px 16px;
    margin-bottom: 20px;
  }

  .buttons-container {
    padding: 0 10px;
  }

  .back-btn {
    min-width: 180px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }

  .continue-btn {
    min-width: 220px;
    padding: 16px 28px;
    font-size: 16px;
    font-weight: 700;
  }

  .arrow-icon {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .student-id-ver-container {
    padding: 8px;
    padding-bottom: 140px;
    margin-top: 100px; /* Still significant top margin on mobile */
  }

  .form-section {
    padding: 16px;
    margin-bottom: 16px;
  }

  .terms-container {
    padding: 16px;
    margin: 0 auto 80px auto;
  }

  .upload-area {
    padding: 24px 12px;
    margin-bottom: 16px;
  }

  .buttons-container {
    padding: 0 8px;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .back-btn {
    min-width: 160px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
    order: 2;
    width: 100%;
  }

  .continue-btn {
    min-width: 200px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 700;
    order: 1;
    width: 100%;
  }

  .arrow-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
